import { processEnvServer } from "hooks/useSsrHooks";
import { AppEnv } from "@RHCommerceDev/utils-app-env";
import { checkCountryIsEu } from "utils/checkCountryIsEu";
import { ESTORE_APP_ID } from "utils/constants";
import { getReqContext } from "utils/reqContext";

const env = globalThis._env;

export type CustomGlobals = AppEnv;

export type CustomGlobalKeys = keyof AppEnv;

export interface GlobalGetters {
  _env: Function;
}

export interface CachedGlobals {
  _env?: AppEnv;
}

const parseVal = (val: string) =>
  val === "true" ? true : val === "false" ? false : val;

export function getEnvOrCookie(key: string): string | boolean {
  let cookiesArray;
  if (typeof document !== "undefined") {
    cookiesArray = document?.cookie?.split(";") ?? [];
  }

  const [_, value] = (cookiesArray?.find(c => c?.includes(`${key}=`)) || "")
    .trim()
    .split("=");
  return parseVal(value ? value : (globalThis._env || {})[key]);
}

/**
 *
 * @param primaryFlag string | only use primaryFlag if preferredFlag is undefined
 * @param preferredFlag string | use preferredFlag if defined
 * @returns boolean
 */
export function getPreferredFlag(
  primaryFlag: string,
  preferredFlag: string
): boolean {
  const primary = getEnvOrCookie(primaryFlag);
  const preferred = getEnvOrCookie(preferredFlag);
  /** it is important the the preferred flag is undefined so primary takes over */
  return Boolean(preferred !== undefined ? preferred : primary);
}

export function mapKeysToEnvOrCookie(keys: (keyof AppEnv)[]): {
  [key in keyof AppEnv]?: string;
} {
  return keys.reduce((prev, key) => {
    prev[key] = getEnvOrCookie(key);
    return prev;
  }, {});
}

export const globalGetters: GlobalGetters = {
  _env: (loc?: string): AppEnv => {
    if (process.env.NODE_ENV === "test") {
      // @ts-ignore
      return process.env;
    } else {
      // server will provide "loc"
      // if !server, then "loc" will be undefined and should be set to window.location.origin for client
      let windowOrigin;
      if (typeof window !== "undefined") {
        windowOrigin = window.location;
      }
      const location = loc || windowOrigin;

      let cookiesArray;
      if (typeof document !== "undefined") {
        cookiesArray = document?.cookie?.split(";") ?? [];
      }

      let featureSSR: boolean | undefined = undefined;
      if (
        cookiesArray?.filter(cookie => cookie?.includes("FEATURE_SSR=true"))
          .length !== 0
      ) {
        featureSSR = true;
      }
      if (
        cookiesArray?.filter(cookie => cookie?.includes("FEATURE_SSR=false"))
          .length !== 0
      ) {
        featureSSR = false;
      }

      let forceClientRender;
      if (typeof window !== "undefined") {
        forceClientRender = window.location.href?.includes("&ct=true");
      }
      const forceCSR = forceClientRender;

      const featureSchedulingVirtualAgent =
        cookiesArray?.filter(cookie =>
          cookie?.includes("FEATURE_SCHEDULING_VIRTUAL_AGENT=true")
        ).length !== 0;

      const keys: (keyof AppEnv)[] = [
        "ABTEST_PG_2UP",
        "APP_ID",
        "FEATURE_ADDON_MESSAGING",
        "FEATURE_EMAIL_MARKETING_FOOTER_NA",
        "FEATURE_ADD_ITEM_CREATE_CART",
        "FEATURE_AEM_SSR",
        "FEATURE_APP_REFRESH_POLLING",
        "FEATURE_MONTREAL",
        "FEATURE_CANONICAL_URLS",
        "FEATURE_CART_CONCIERGE_SAVED_PAYMENT_METHODS",
        "FEATURE_CART_ID_FOR_SESSION",
        "FEATURE_CART_ID_ATOM",
        "FEATURE_CART_ID_SESSION",
        "FEATURE_CART_PRICE_DEBUG",
        "FEATURE_CART_PRICE_DISPLAY",
        "FEATURE_CART_REMOVE_SHIPPING_AMOUNT",
        "FEATURE_CART_RESTRICTIONS",
        "FEATURE_CART_SPO",
        "FEATURE_CHECKOUT_SHIPPING_BUTTON_V2",
        "FEATURE_CART_PBB_SENT_FOR_CLEARING",
        "FEATURE_CG_CAROUSEL",
        "FEATURE_CG_PAGINATION",
        "FEATURE_CG_PAGINATION_LOAD_ALL",
        "FEATURE_CG_PAGINATION_LOAD_FULL_PAGE",
        "FEATURE_CHECKOUT_REDIRECT_DISABLE",
        "FEATURE_CMR",
        "FEATURE_COUNTRY_NAV_MENU",
        "FEATURE_DISPLAY_MISSING_AEM_PAGE_CONTENT_KEYS",
        "FEATURE_DISPLAY_MISSING_RESOURCE_PAGE_CONTENT_KEYS",
        "FEATURE_EMAIL_MARKETING_FOOTER_BE",
        "FEATURE_EMAIL_MARKETING_FOOTER_FR",
        "FEATURE_EMAIL_MARKETING_POPUP_FR",
        "FEATURE_EMAIL_MARKETING_POPUP_NA",
        "FEATURE_EU_EXPANSION_FR",
        "FEATURE_EMAIL_MARKETING_FOOTER_UK",
        "FEATURE_EU_PRICE_FORMAT",
        "FEATURE_FASTER_CHECKOUT_SIGN_IN",
        "FEATURE_FRANCE_288_ECO_FEE",
        "FEATURE_FRANCE_544_EXPORT_ORDER",
        "FEATURE_FRANCE_391_PARTIAL_PAYMENT_PORTAL",
        "FEATURE_IP_BASED_REDIRECT",
        "FEATURE_MY_ACCOUNT_PAYMENT_FORM",
        "FEATURE_PDP",
        "FEATURE_PDP_CART_DELIVERY",
        "FEATURE_PERFORMANCE_SESSION_MEMBERSHIP",
        "FEATURE_IFRAME_PRINT",
        "FEATURE_SAVE_CARD",
        "FEATURE_SANCTIONED_COUNTRIES",
        "FEATURE_SEARCH_ALL_BRANDS",
        "FEATURE_CONCEPT_TABS",
        "FEATURE_SERVICE_WORKER",
        "FEATURE_SPLUNK_RUM",
        "FEATURE_SPLUNK_SESSION_RECORDER",
        "FEATURE_SSR",
        "FEATURE_SSR_WITH_CLIENT_RENDER",
        "FEATURE_SUPPRESS_UK",
        "FEATURE_USE_PLACEHOLDER_IMAGES",
        "FEATURE_USE_QUANTITY_IN_STOCK",
        "FEATURE_AEM_RENDER_CONTROL",
        "FEATURE_WEB_VITALS",
        "PAY_BY_BANK_FRAME_ORIGIN",
        "PAY_BY_BANK_SCRIPT",
        "FEATURE_ANALYTICS_FORM_DATALAYER",
        "REMOVE_ANALYTICS_LOADER",
        "FEATURE_AN_864_GOOGLE_CONSENT_MODE_V2",
        "FEATURE_INTERNATIONAL_LANGUAGE_PREFERENCE",
        "FEATURE_ALWAYS_SSR",
        "FEATURE_BYPASS_STAGGER_LOAD",
        "FEATURE_LOAD_PERFORMANCE",
        "FEATURE_URL_CHANGE",
        "FEATURE_BCT_SUNSET",
        "FEATURE_PO_BOX_CHECK",
        "FEATURE_CART_SHIP_ADDRESS_DELETE",
        "FEATURE_ADDRESS_SAVED_VALIDATION",
        "FEATURE_PAYMENT_PORTAL_CCD",
        "FEATURE_INT_COOKIE_PREFERENCE",
        "FEATURE_RH_PROMO_BANNER",
        "FEATURE_PG_DEFAULT_ITEMS_PER_PAGE",
        "FEATURE_PDP_REDESIGN",
        "FEATURE_ROOMS_CAROUSEL_FIX",
        "FEATURE_STYTCH_TEST",
        "FEATURE_STYTCH_BFF",
        "FEATURE_PREBILL_UI",
        "FEATURE_BFF_ENDPOINTS_EXPOSER",
        "FEATURE_TAX_EXEMPT_OR_VAT_NUMBER",
        "FEATURE_EU_MONACO",
        "DISABLE_VALID_ASSOCIATE",
        "FEATURE_CART_PRICE_LOCK_UP_FORMAT_NA",
        "FEATURE_SR_4334_AKAMAI_RELOAD",
        "FEATURE_CART_PRICE_LOCK_UP_FORMAT_EU",
        "FEATURE_SR_4382_RADIX_SELECT",
        "FEATURE_GET_ADDRESS_PAGE_CART",
        "FEATURE_HIDE_RELACEMENT_PARTS",
        "FEATURE_SR_4451_CG_SWATCH_HOVER_COLORIZATION",
        "FEATURE_RENDER_ALL",
        "FEATURE_SR_4702_CONSTRUCTOR",
        "FEATURE_SR_4657_IMAGE_FALLBACK",
        "FEATURE_TEST_PRESET",
        "FEATURE_PP_131_NEW_CART_PANEL",
        "FEATURE_PDP_IMAGE_OPTIMIZATIONS",
        "FEATURE_NEW_HEADER",
        "FEATURE_NEW_SWATCH_FIELDS",
        "FEATURE_PDP_IMAGE_OPTIMIZATIONS",
        "FEATURE_PP_498_SPO_CHECKOUT",
        "FORCE_GUESTHOUSE_MODE",
        "FEATURE_COLORIZATION",
        "FEATURE_NEW_GOOGLE_PLACES_API",
        "FEATURE_ALGOLIA_SEARCH",
        "FEATURE_SALE_PRICE",
        "OPENSEARCH_API",
        "FEATURE_PRICE_COMMA",
        "APP_ALGOLIA_APP_ID"
      ];

      const REACT_APP_KEYCLOAK_CLIENTID =
        checkCountryIsEu({
          country: getCountryFromUrl()
        }) && env?.APP_ID == ESTORE_APP_ID
          ? env?.REACT_APP_KEYCLOAK_CLIENTID_UK
          : env?.REACT_APP_KEYCLOAK_CLIENTID;

      const getSSRKeys = () => {
        const FEATURE_AEM_SSR = getEnvOrCookie("FEATURE_AEM_SSR");

        const FEATURE_AEM_SSR_WITH_CLIENT_RENDER = forceCSR
          ? "false"
          : FEATURE_AEM_SSR;

        const FEATURE_SSR = getEnvOrCookie("FEATURE_SSR");

        const FEATURE_SSR_WITH_CLIENT_RENDER = forceCSR ? "false" : FEATURE_SSR;

        const ssrKeys = {
          FEATURE_AEM_SSR_WITH_CLIENT_RENDER,
          FEATURE_AEM_SSR,
          FEATURE_SSR,
          FEATURE_SSR_WITH_CLIENT_RENDER
        };

        return ssrKeys;
      };

      const args = Object.keys(env || {}) // Add a check for null or undefined
        .map(k => ({ [k]: parseVal(env?.[k]) })) // Use optional chaining for null/undefined safety
        .concat({
          ...mapKeysToEnvOrCookie(keys),
          ...getSSRKeys(),

          REACT_APP_BFF_ORIGIN: env?.REACT_APP_BFF_ORIGIN,
          FEATURE_SCHEDULING_VIRTUAL_AGENT: featureSchedulingVirtualAgent
            ? "true"
            : env?.FEATURE_SCHEDULING_VIRTUAL_AGENT,
          REACT_APP_KEYCLOAK_CLIENTID,
          FEATURE_CG_TEXT_STYLING_0425: getEnvOrCookie(
            "FEATURE_CG_TEXT_STYLING_0425"
          ),
          FEATURE_DYNAMIC_GAP_CALCULATION: getEnvOrCookie(
            "FEATURE_DYNAMIC_GAP_CALCULATION"
          )
        });

      let _env = Object.assign({}, ...args);

      /** custom event implementation requires because country change doesn't trigger page reload */
      if (typeof window !== "undefined") {
        window?.addEventListener("country", (cevent: CustomEvent) => {
          const REACT_APP_KEYCLOAK_CLIENTID =
            checkCountryIsEu({
              country: cevent?.detail?.country
            }) && env?.APP_ID == ESTORE_APP_ID
              ? env?.REACT_APP_KEYCLOAK_CLIENTID_UK
              : env?.REACT_APP_KEYCLOAK_CLIENTID;

          _env = Object.assign(_env, { REACT_APP_KEYCLOAK_CLIENTID });
        });
      }

      return _env;
    }
  }
};

export const cachedGlobals: CachedGlobals = {};

export const getGlobal = (
  name: keyof GlobalGetters,
  loc?: string
): CustomGlobals => {
  // globalGetters will only be ran once on client and once on server
  return loc || typeof cachedGlobals[name] === "undefined"
    ? (cachedGlobals[name] = globalGetters[name](loc))
    : cachedGlobals[name];
};

/** copy pasted the code from getCountryFromUrl because import the utility throwing an error */
export const getCountryFromUrl = (): string => {
  const req = getReqContext();

  let urlCountry = "";
  if (processEnvServer) {
    urlCountry = req?.url?.split("/")[1];
  } else {
    let url;
    if (typeof window !== "undefined") {
      url = new URL(window.location.href);
    }
    urlCountry = url ? url.pathname.split("/")[1] : "us";
  }

  let countrySelection: string = "US";
  if (
    ["/ca/", "/us/", "/gb/", "/de/", "/be/", "/es/", "/fr/"]?.includes(
      `/${urlCountry}/`
    )
  ) {
    countrySelection = urlCountry?.toUpperCase();
  }
  return countrySelection;
};
